import { RuleForm, RuleFormStatus } from '@innovatm/js-rule-editor';
import { styled } from '@mui/material';
import Box from '@mui/material/Box';
import React, { memo, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
    GetGetRulesResponse,
    IRuleGeneric,
    KeyGetGetRules,
    useMutationPostCreateRule,
    useMutationPutUpdateRule,
} from '../../backend';
import { useCurrentUserClient } from '../../contexts/OidcContext/OidcContext.selectors';
import { ROUTE_EDITOR } from '../../routes';
import { errorToastConfig, successToastConfig } from '../../utils/utils';
import { RoundButton } from '../Button/RoundButton/RoundButton';
import { StyledRuleEditorButtonsBox } from './RuleEditor.style';
import { RuleEditorExportButton } from './RuleEditorExportButton';
import { editorTitles, formFields, initialValues, validatedForms } from './RulesTypes';

export const StyledUnknownBox = styled(Box)`
    display: flex;
    flex-direction: column;
    width: 68%;
    padding: 20px;
    align-items: center;
    color: white;
`;
export const EditorUnknownType = memo(({ type }: { type?: string }) => (
    <StyledUnknownBox>Unknown type "{type}"</StyledUnknownBox>
));
export const EditorUnknownRule = memo(({ name }: { name?: string }) => (
    <StyledUnknownBox>Unknown rule "{name}"</StyledUnknownBox>
));

export const RuleEditorNew = memo(({ rules }: { rules?: IRuleGeneric[] }) => {
    const client = useCurrentUserClient();
    const navigate = useNavigate();
    const onCancel = useCallback(() => navigate(ROUTE_EDITOR), [navigate]);

    const queryClient = useQueryClient();

    const { mutate: postCreateRule, isLoading } = useMutationPostCreateRule({
        onSuccess: ({ data: rule }) => {
            navigate(ROUTE_EDITOR);
            queryClient.setQueriesData(
                { queryKey: [KeyGetGetRules, { context: client.airportIata }] },
                (values: GetGetRulesResponse): GetGetRulesResponse => ({
                    ...values,
                    data: values.data.concat([rule]),
                }),
            );
            toast(`The rule ${rule.name} is successfully created.`, successToastConfig);
        },
        onError: error => {
            toast(
                `[${(error?.response?.data as any)?.status}] ${(error?.response?.data as any)?.message}`,
                errorToastConfig,
            );
        },
    });
    const createRule = useCallback(
        rule => {
            postCreateRule({
                nonEditableRuleResourceManagerRuleRule: rule,
                context: client.airportIata,
            });
        },
        [client.airportIata, postCreateRule],
    );

    const { type, name } = useParams();

    if (!type || !initialValues[type] || !formFields[type]) {
        return <EditorUnknownType type={type} />;
    }

    if (!name) {
        return (
            <RuleForm key={type} initialValues={initialValues[type]} onSubmit={createRule}>
                {editorTitles[type]}
                {formFields[type]}

                <StyledRuleEditorButtonsBox>
                    <RoundButton color="primary" size="medium" variant="contained" loading={isLoading} type="submit">
                        <FormattedMessage id={'ruleEditor.editor.create'} />
                    </RoundButton>
                    &emsp;
                    <RoundButton color="primary" size="medium" variant="outlined" onClick={onCancel}>
                        <FormattedMessage id={'resourcesForm.cancel'} />
                    </RoundButton>
                </StyledRuleEditorButtonsBox>
            </RuleForm>
        );
    } else {
        const rule = rules?.find(r => r.type === type && r.name === name);
        if (!rule) {
            return <EditorUnknownRule name={name} />;
        }

        const ValidatedRuleForm = validatedForms[type] || RuleForm;

        return (
            <ValidatedRuleForm key={type} initialValues={rule as any} onSubmit={createRule}>
                {editorTitles[type]}
                {formFields[type]}

                <StyledRuleEditorButtonsBox>
                    <RoundButton color="primary" size="medium" variant="contained" loading={isLoading} type="submit">
                        <FormattedMessage id={'ruleEditor.editor.create'} />
                    </RoundButton>
                    &emsp;
                    <RuleEditorExportButton />
                    &emsp;
                    <RoundButton color="primary" size="medium" variant="outlined" onClick={onCancel}>
                        <FormattedMessage id={'resourcesForm.cancel'} />
                    </RoundButton>
                </StyledRuleEditorButtonsBox>
            </ValidatedRuleForm>
        );
    }
});

const ruleFormStatus: RuleFormStatus = {
    disabledNameField: true,
};

export const RuleEditorEdit = memo(({ rules }: { rules?: IRuleGeneric[] }) => {
    const client = useCurrentUserClient();
    const navigate = useNavigate();
    const onCancel = useCallback(() => navigate(ROUTE_EDITOR), [navigate]);

    const queryClient = useQueryClient();

    const { mutate: postUpdateRule, isLoading } = useMutationPutUpdateRule({
        onSuccess: ({ data: rule }) => {
            navigate(ROUTE_EDITOR);
            queryClient.setQueriesData(
                { queryKey: [KeyGetGetRules, { context: client.airportIata }] },
                (values: GetGetRulesResponse): GetGetRulesResponse => ({
                    ...values,
                    data: values.data.map(r => (r.name === rule.name ? rule : r)),
                }),
            );
            toast(`The rule ${rule.name} is successfully updated.`, successToastConfig);
        },

        onError: error => {
            Object.keys(error?.response?.data).map(errorKey =>
                toast(`${error?.response?.data[errorKey]}`, errorToastConfig),
            );
        },
    });
    const updateRule = useCallback(
        rule => {
            postUpdateRule({
                nonEditableRuleResourceManagerRuleRule: rule,
                context: client.airportIata,
            });
        },
        [client.airportIata, postUpdateRule],
    );

    const { type, name } = useParams();

    if (!type) {
        return <EditorUnknownType type={type} />;
    }

    const rule = rules?.find(r => r.type === type && r.name === name);
    if (!rule) {
        return <EditorUnknownRule name={name} />;
    }

    const ValidatedRuleForm = validatedForms[type] || RuleForm;

    return (
        <ValidatedRuleForm
            key={rule.name}
            initialValues={rule as any}
            initialStatus={ruleFormStatus}
            onSubmit={updateRule}
        >
            {editorTitles[type]}
            {formFields[type]}

            <StyledRuleEditorButtonsBox>
                <RoundButton color="primary" size="medium" variant="contained" loading={isLoading} type="submit">
                    <FormattedMessage id={'ruleEditor.editor.update'} />
                </RoundButton>
                &emsp;
                <RoundButton
                    color="primary"
                    size="medium"
                    variant="contained"
                    onClick={() => navigate(`${ROUTE_EDITOR}/test/${rule.name}`)}
                >
                    <FormattedMessage id={'ruleEditor.editor.test'} />
                </RoundButton>
                &emsp;
                <RuleEditorExportButton />
                &emsp;
                <RoundButton color="primary" size="medium" variant="outlined" onClick={onCancel}>
                    <FormattedMessage id={'resourcesForm.cancel'} />
                </RoundButton>
            </StyledRuleEditorButtonsBox>
        </ValidatedRuleForm>
    );
});
